import React, { useMemo } from "react"
import Avatar from "react-avatar"
import classNames from "classnames"
import css from "./avatarImage.module.css"
import { Participant } from "../reducers/chatsSlice"
import { Icon } from "@blueprintjs/core"
import useImageCache from "../hooks/useImageCache"

type AvatarProps = {
  name: string
  size?: "xxsmall" | "xsmall" | "small" | "large" | "xlarge"
  className?: string
  src?: string
  clickable?: boolean
  active?: boolean
  color?: string
  useCache?: boolean
}

export const participantPhotoToSrc = (photo: Participant["photo"]) => {
  if (!photo) return
  return photo.url ? photo.url : `data:${photo.type};base64, ${photo.data}`
}

const AvatarImage = ({
  name,
  size = "small",
  className,
  src,
  clickable,
  active,
  color,
  useCache = true,
}: AvatarProps) => {
  // Use our image cache hook
  const { cachedSrc, isLoading } = useImageCache(useCache ? src : undefined)

  // Memoize size calculation to prevent recalculations on re-render
  const sizePx = useMemo(() => {
    if (size === "xxsmall") return "12px"
    if (size === "xsmall") return "20px"
    if (size === "large") return "50px"
    if (size === "xlarge") return "100px"
    return "30px"
  }, [size])

  // Memoize text size ratio calculation
  const textSizeRatio = useMemo(() => {
    if (size === "xxsmall") return 1
    if (size === "xsmall") return 2
    if (size === "large") return 3
    if (size === "xlarge") return 5
    return 2.5
  }, [size])

  // Memoize alt text
  const avatarAlt = useMemo(() => {
    return active ? `${name}, driving` : name
  }, [active, name])

  // Memoize class names to prevent recalculation
  const avatarClassName = useMemo(
    () =>
      classNames(css.avatar, className, {
        [css.clickable]: clickable,
        [css.large]: size === "large",
        [css.xxsmall]: size === "xxsmall",
        [css.xsmall]: size === "xsmall",
        [css.small]: size === "small",
        [css.xlarge]: size === "xlarge",
        [css.loading]: isLoading,
      }),
    [className, clickable, isLoading, size],
  )

  const containerClassName = useMemo(
    () =>
      classNames(css.avatarContainer, {
        [css.activeContainer]: active,
      }),
    [active],
  )
  return (
    <div className={containerClassName}>
      {active && (
        <div className={css.active}>
          <Icon icon="double-chevron-right" />
        </div>
      )}
      <Avatar
        name={name}
        className={avatarClassName}
        maxInitials={2}
        round={true}
        size={sizePx}
        textSizeRatio={textSizeRatio}
        color={color || css.defaultAvatarColor}
        src={useCache ? cachedSrc : src} // Use the cached source instead of direct src
        alt={avatarAlt}
        title={avatarAlt}
      />
    </div>
  )
}

export default React.memo(AvatarImage)
